import {cn} from "@adleritech/web-ui/utils";
import {useEffect, useId} from "react";
import styles from "./Modal.module.css";

export type ModalProps = {
    show: boolean;
    onHide: () => void;
    children: React.ReactNode;
    className?: string;
};

export function Modal({show, onHide, children, className}: ModalProps) {
    const modalId = useId();

    useEffect(() => {
        const previousOverflow = document.body.style.overflow;

        if (show) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = previousOverflow;
        }
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [show]);

    useEffect(() => {
        if (!show) return;

        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                onHide();
            }
        };
        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [show, onHide]);

    if (!show) return null;

    return (
        <div className={styles.modalBackdrop}>
            <div
                className={cn(styles.modalDialog, className)}
                role="dialog"
                aria-modal="true"
                aria-labelledby={`${modalId}-title`}
            >
                {children}
            </div>
        </div>
    );
}

export function ModalHeader({
    children,
    closeButton,
    onHide,
}: {
    children: React.ReactNode;
    closeButton?: boolean;
    onHide?: () => void;
}) {
    return (
        <div className={styles.modalHeader}>
            {children}
            {closeButton && onHide && (
                <button
                    className={styles.closeButton}
                    onClick={onHide}
                    type="button"
                    aria-label="Close modal"
                >
                    ×
                </button>
            )}
        </div>
    );
}

export function ModalTitle({children}: {children: React.ReactNode}) {
    return <div className={styles.modalTitle}>{children}</div>;
}

export function ModalBody({children}: {children: React.ReactNode}) {
    return <div className={styles.modalBody}>{children}</div>;
}

export function ModalFooter({children}: {children: React.ReactNode}) {
    return <div className={styles.modalFooter}>{children}</div>;
}

Modal.Header = ModalHeader;
Modal.Title = ModalTitle;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
